import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from '@/store';
import { fetchProgramLeadDashboard } from '@/store/slices/dashboardSlice';
import { fetchPrograms } from '@/store/slices/programsSlice';
import { fetchPlanningPeriods } from '@/store/slices/planningPeriodsSlice';
import { fetchScenarios } from '@/store/slices/scenariosSlice';
import { formatPercent, formatTeamWeeks } from '@/utils/formatters';

// Design System Components
import {
  GlassPageHeader,
  GlassCard,
  GlassSelect,
  MetricCard,
  SectionReveal,
  CollapsibleMetricsSection,
} from '@/design-system';

// Icons
import { Users, TrendingUp, Layers, GitBranch, Target, Building2 } from 'lucide-react';

// Child Components (unchanged)
import EpicAccountabilityView from './components/EpicAccountabilityView';
import CapacityUtilizationCharts from './components/CapacityUtilizationCharts';
import CategoryComplianceMonitoring from './components/CategoryComplianceMonitoring';
import CrossProgramCoordinationView from './components/CrossProgramCoordinationView';

const ProgramLeadDashboardPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { programLead } = useSelector((state: RootState) => state.dashboard);
  const { isLoading } = programLead;
  const { programs } = useSelector((state: RootState) => state.programs);
  const { periods: planningPeriods } = useSelector((state: RootState) => state.planningPeriods);
  const { scenarios } = useSelector((state: RootState) => state.scenarios);

  const [selectedProgram, setSelectedProgram] = useState<string>('');
  const [selectedPlanningPeriod, setSelectedPlanningPeriod] = useState<string>('');
  const [selectedScenario, setSelectedScenario] = useState<string>('');

  useEffect(() => {
    dispatch(fetchPlanningPeriods());
    dispatch(fetchPrograms({}));
  }, [dispatch]);

  useEffect(() => {
    if (selectedPlanningPeriod) {
      dispatch(fetchScenarios({ planningPeriodId: selectedPlanningPeriod }));
    }
  }, [dispatch, selectedPlanningPeriod]);

  useEffect(() => {
    if (planningPeriods.length > 0 && !selectedPlanningPeriod) {
      const activePeriod = planningPeriods.find((p: any) => p.isActive);
      setSelectedPlanningPeriod(activePeriod?.id || planningPeriods[0]?.id || '');
    }
  }, [planningPeriods, selectedPlanningPeriod]);

  useEffect(() => {
    if (scenarios.length > 0 && !selectedScenario) {
      const baselineScenario = scenarios.find((s: any) => s.isBaseline);
      setSelectedScenario(baselineScenario?.id || scenarios[0]?.id || '');
    }
  }, [scenarios, selectedScenario]);

  useEffect(() => {
    if (selectedProgram && selectedPlanningPeriod) {
      dispatch(fetchProgramLeadDashboard({
        programId: selectedProgram,
        planningPeriodId: selectedPlanningPeriod,
        scenarioId: selectedScenario || undefined,
      }));
    }
  }, [dispatch, selectedProgram, selectedPlanningPeriod, selectedScenario]);

  // Build options for GlassSelect (which expects { value, label })
  const programOptions = [
    { value: '', label: 'Select Program' },
    ...programs.map((program: any) => ({
      value: program.id,
      label: program.name,
    })),
  ];

  const planningPeriodOptions = [
    { value: '', label: 'Select Planning Period' },
    ...planningPeriods.map((period: any) => ({
      value: period.id,
      label: `${period.name}${period.isActive ? ' (Active)' : ''}`,
    })),
  ];

  const scenarioOptions = [
    { value: '', label: 'All Scenarios' },
    ...scenarios.map(scenario => ({
      value: scenario.id,
      label: `${scenario.name}${scenario.isBaseline ? ' (Baseline)' : ''}`,
    })),
  ];

  const selectedProgramData = programs.find((p: any) => p.id === selectedProgram);

  // Extract metrics for the summary section
  const primaryEpicsCount = (programLead.epicAccountability as any)?.primaryEpics?.length || 0;
  const contributingEpicsCount = (programLead.epicAccountability as any)?.contributingEpics?.length || 0;
  const utilizationPercentage = programLead.capacityUtilization?.utilizationPercentage || 0;
  const teamCount = programLead.capacityUtilization?.teamCount || 0;
  const totalCapacity = programLead.capacityUtilization?.totalCapacity || 0;
  const coordinationPointsCount = (programLead.crossProgramCoordination as any)?.coordinationPoints?.length || 0;
  const allocatedEpicsCount = programLead.allocatedEpicsCount || 0;
  const supportedISCount = programLead.supportedISCount || 0;

  // Determine utilization status
  const getUtilizationStatus = () => {
    if (utilizationPercentage > 100) return 'Over-allocated';
    if (utilizationPercentage > 90) return 'High utilization';
    return 'Good utilization';
  };

  const getUtilizationColor = (): 'rose' | 'amber' | 'emerald' => {
    if (utilizationPercentage > 100) return 'rose';
    if (utilizationPercentage > 90) return 'amber';
    return 'emerald';
  };

  return (
    <div className="glass-page-bg min-h-screen p-6 lg:p-8">
      {/* Page Header */}
      <SectionReveal delay={0}>
        <GlassPageHeader
          title="Program Lead Dashboard"
          subtitle={selectedProgramData ?
            `Delivery tracking for ${selectedProgramData.name}` :
            'Select a program to view delivery metrics and coordination details'
          }
          icon={<Users className="w-6 h-6" />}
          iconGradient="blue-violet"
          className="mb-6"
          actions={
            <div className="flex items-center gap-3 flex-wrap">
              <GlassSelect
                label="Program"
                value={selectedProgram}
                onChange={(value) => setSelectedProgram(value)}
                options={programOptions}
                size="sm"
              />
              <GlassSelect
                label="Planning Period"
                value={selectedPlanningPeriod}
                onChange={(value) => setSelectedPlanningPeriod(value)}
                options={planningPeriodOptions}
                size="sm"
              />
              <GlassSelect
                label="Scenario"
                value={selectedScenario}
                onChange={(value) => setSelectedScenario(value)}
                options={scenarioOptions}
                size="sm"
              />
            </div>
          }
        />
      </SectionReveal>

      {!selectedProgram ? (
        /* Empty State */
        <SectionReveal delay={0.1}>
          <GlassCard variant="default" padding="lg">
            <div className="text-center py-12">
              <Users className="w-16 h-16 mx-auto mb-4 text-muted-foreground/60" />
              <h3 className="text-lg font-medium text-foreground mb-2">Select a Program</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Choose a program from the dropdown above to view EPIC accountability,
                capacity utilization, category compliance, and cross-program coordination.
              </p>
            </div>
          </GlassCard>
        </SectionReveal>
      ) : (
        <>
          {/* EPIC Accountability View */}
          <SectionReveal delay={0.1}>
            <div className="mb-6">
              <EpicAccountabilityView
                epicAccountability={programLead.epicAccountability as any}
                isLoading={isLoading}
              />
            </div>
          </SectionReveal>

          {/* Main Content Grid: Capacity Utilization + Category Compliance */}
          <SectionReveal delay={0.15}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Capacity Utilization */}
              <CapacityUtilizationCharts
                capacityUtilization={programLead.capacityUtilization}
                isLoading={isLoading}
              />

              {/* Category Compliance */}
              <CategoryComplianceMonitoring
                categoryCompliance={programLead.categoryCompliance}
                isLoading={isLoading}
              />
            </div>
          </SectionReveal>

          {/* Cross-Program Coordination */}
          <SectionReveal delay={0.2}>
            <div className="mb-6">
              <CrossProgramCoordinationView
                crossProgramCoordination={programLead.crossProgramCoordination as any}
                isLoading={isLoading}
              />
            </div>
          </SectionReveal>

          {/* Program Performance Summary with MetricCards */}
          <SectionReveal delay={0.3}>
            <GlassCard variant="default" padding="lg">
              <h3 className="text-lg font-semibold text-foreground mb-6">Program Performance Summary</h3>
              <CollapsibleMetricsSection storageKey="program-lead-dashboard">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                  <MetricCard
                    title="EPICs with Allocations"
                    value={allocatedEpicsCount}
                    subtitle="Total EPICs receiving capacity"
                    icon={<Target className="w-5 h-5" />}
                    iconColor="emerald"
                  />
                  <MetricCard
                    title="ISs Supported"
                    value={supportedISCount}
                    subtitle="Investible Structures served"
                    icon={<Building2 className="w-5 h-5" />}
                    iconColor="amber"
                  />
                  <MetricCard
                    title="Primary EPICs"
                    value={primaryEpicsCount}
                    subtitle={`${contributingEpicsCount} contributing`}
                    icon={<Layers className="w-5 h-5" />}
                    iconColor="blue"
                  />
                  <MetricCard
                    title="Capacity Utilization"
                    value={formatPercent(utilizationPercentage)}
                    subtitle={getUtilizationStatus()}
                    icon={<TrendingUp className="w-5 h-5" />}
                    iconColor={getUtilizationColor()}
                  />
                  <MetricCard
                    title="Teams"
                    value={teamCount}
                    subtitle={`${formatTeamWeeks(totalCapacity)} capacity`}
                    icon={<Users className="w-5 h-5" />}
                    iconColor="cyan"
                  />
                  <MetricCard
                    title="Coordination Points"
                    value={coordinationPointsCount}
                    subtitle="Cross-program dependencies"
                    icon={<GitBranch className="w-5 h-5" />}
                    iconColor="violet"
                  />
                </div>
              </CollapsibleMetricsSection>
            </GlassCard>
          </SectionReveal>
        </>
      )}
    </div>
  );
};

export default ProgramLeadDashboardPage;
